import { ProfileItem } from '@/components/profile-item'
import { useAuth } from '@/components/providers/auth-provider'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { useTRPC } from '@/trpc/react'
import { getDisplayEmail, getDisplayName } from '@repo/shared_utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import {
  FaBuilding,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaEnvelope,
  FaGoogle,
  FaIdCard,
  FaTimesCircle,
  FaUser,
  FaUserTag,
} from 'react-icons/fa'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/admin/user_profile/')({
  component: RouteComponent,
})

function RouteComponent() {
  const { user, isPending } = useAuth()
  const api = useTRPC()
  const queryClient = useQueryClient()

  const { mutate: updateEmailMasking } = useMutation({
    ...api.user.updateEmailMasking.mutationOptions(),
    onSuccess: () => {
      toast.success('Email masking preference updated')
      queryClient.invalidateQueries()
    },
    onError: () => {
      toast.error('Failed to update email masking preference')
    },
  })

  const { mutate: updateNameMasking } = useMutation({
    ...api.user.updateNameMasking.mutationOptions(),
    onSuccess: () => {
      toast.success('Name masking preference updated')
      queryClient.invalidateQueries()
    },
    onError: () => {
      toast.error('Failed to update name masking preference')
    },
  })

  if (isPending) {
    return (
      <div className="container py-10">
        <Card className="mx-auto max-w-4xl shadow-lg border-0 bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
          <CardHeader className="pb-6">
            <CardTitle className="flex items-center gap-4 text-2xl">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-8 w-40" />
            </CardTitle>
          </CardHeader>
          <Separator className="mb-6" />
          <CardContent>
            <div className="grid gap-6 md:grid-cols-2">
              {[...Array(8)].map((_, i) => (
                <div
                  className="flex items-start gap-4 p-4 rounded-lg bg-white/50 dark:bg-gray-800/50"
                  key={i}
                >
                  <Skeleton className="h-5 w-5 rounded-full mt-1" />
                  <div className="flex flex-col space-y-2 w-full">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-5 w-32" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container py-10">
      <Card className="mx-auto max-w-4xl shadow-lg border-0 bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-4 text-2xl">
            <div className="p-2 rounded-full">
              <FaGoogle className="h-6 w-6" />
            </div>
            User Profile
          </CardTitle>
        </CardHeader>
        <Separator className="mb-6" />
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="p-4 rounded-lg bg-white/70 dark:bg-gray-800/70 hover:bg-white dark:hover:bg-gray-800 transition-colors">
              <ProfileItem
                label="Full Name"
                value={getDisplayName(user?.name ?? '', user?.maskName ?? false)}
                icon={FaUser}
              />
            </div>

            <div className="p-4 rounded-lg bg-white/70 dark:bg-gray-800/70 hover:bg-white dark:hover:bg-gray-800 transition-colors">
              <ProfileItem
                label="Email Address"
                value={getDisplayEmail(user?.email ?? '', user?.maskEmail ?? false)}
                icon={FaEnvelope}
                copyToClipboard
              />
            </div>

            <div className="p-4 rounded-lg bg-white/70 dark:bg-gray-800/70 hover:bg-white dark:hover:bg-gray-800 transition-colors">
              <ProfileItem
                label="User ID"
                value={user?.userId ?? ''}
                icon={FaIdCard}
                copyToClipboard
              />
            </div>

            <div className="p-4 rounded-lg bg-white/70 dark:bg-gray-800/70 hover:bg-white dark:hover:bg-gray-800 transition-colors">
              <ProfileItem
                label="Account Created"
                value={DateTime.fromJSDate(user?.createdAt ?? new Date()).toFormat('MMMM d, yyyy')}
                icon={FaCalendarAlt}
              />
            </div>

            <div className="p-4 rounded-lg bg-white/70 dark:bg-gray-800/70 hover:bg-white dark:hover:bg-gray-800 transition-colors">
              <ProfileItem
                label="Last Updated"
                value={DateTime.fromJSDate(user?.updatedAt ?? new Date()).toFormat('MMMM d, yyyy')}
                icon={FaClock}
              />
            </div>

            <div className="p-4 rounded-lg bg-white/70 dark:bg-gray-800/70 hover:bg-white dark:hover:bg-gray-800 transition-colors">
              <div className="flex items-start gap-3">
                {user?.isUserOnboarded ? (
                  <FaCheckCircle className="mt-1 h-5 w-5 text-green-600" />
                ) : (
                  <FaTimesCircle className="mt-1 h-5 w-5 text-red-600" />
                )}
                <div className="flex flex-col space-y-2">
                  <dt className="text-sm font-medium text-muted-foreground">Onboarding Status</dt>
                  <dd>
                    <Badge
                      variant={user?.isUserOnboarded ? 'default' : 'destructive'}
                      className="font-medium"
                    >
                      {user?.isUserOnboarded ? 'Completed' : 'Pending'}
                    </Badge>
                  </dd>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-white/70 dark:bg-gray-800/70 hover:bg-white dark:hover:bg-gray-800 transition-colors">
              <div className="flex items-start gap-3">
                <FaUserTag className="mt-1 h-5 w-5 text-muted-foreground" />
                <div className="flex flex-col space-y-2">
                  <dt className="text-sm font-medium text-muted-foreground">Role</dt>
                  <dd>
                    <Badge variant="outline" className="font-medium capitalize">
                      {user?.role ?? '--'}
                    </Badge>
                  </dd>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-white/70 dark:bg-gray-800/70 hover:bg-white dark:hover:bg-gray-800 transition-colors">
              <ProfileItem
                label="Business ID"
                value={user?.businessId ?? ''}
                icon={FaBuilding}
                copyToClipboard
              />
            </div>
          </div>

          <Separator className="my-8" />

          {/* Privacy Settings Section */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Privacy Settings</h3>
              <div className="p-4 rounded-lg bg-white/70 dark:bg-gray-800/70 hover:bg-white dark:hover:bg-gray-800 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="email-masking" className="text-sm font-medium">
                      Mask Email Address
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Hide parts of your email address in the interface (e.g., j***@example.com)
                    </p>
                  </div>
                  <Switch
                    id="email-masking"
                    checked={user?.maskEmail ?? false}
                    onCheckedChange={(checked) => {
                      updateEmailMasking({ maskEmail: checked })
                    }}
                  />
                </div>
              </div>
              <div className="p-4 rounded-lg bg-white/70 dark:bg-gray-800/70 hover:bg-white dark:hover:bg-gray-800 transition-colors mt-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="name-masking" className="text-sm font-medium">
                      Mask Name
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Hide your full name by showing initials only (e.g., J*** D***)
                    </p>
                  </div>
                  <Switch
                    id="name-masking"
                    checked={user?.maskName ?? false}
                    onCheckedChange={(checked) => {
                      updateNameMasking({ maskName: checked })
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
